import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, FileText, Sparkles, Import, ChevronDown, Check, TrendingUp, Search, BarChart3, Zap, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { MonetizationStepper } from "@/components/monetization/MonetizationStepper";
const PLATFORMS = ["YouTube", "TikTok", "Instagram", "Twitter/X", "Podcast", "Blog", "Email List", "Multiple"];
const SKILL_LEVELS = ["Beginner", "Intermediate", "Advanced", "Mixed"];
const GOALS = [
  "Educate & Teach",
  "Provide Tools & Templates",
  "Offer Transformation",
  "Save Time (Shortcuts)",
  "Deliver Results",
  "Build Community",
];

interface Project {
  id: string;
  title: string;
  entry_type: string;
  niche: string | null;
  audience_type: string | null;
  platform: string | null;
  script_input: string | null;
  idea_input: string | null;
  skill_level: string | null;
  goal: string | null;
  audience_pain_points: string[] | null;
}

interface NexisProject {
  id: string;
  title: string;
  script: string | null;
  created_at: string;
}

export default function MonetizationInput() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState<any | null>(null);
  const [thinkingStep, setThinkingStep] = useState(0);
  const thinkingInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  // Discover audience context
  const [audienceStruggle, setAudienceStruggle] = useState("");
  const [creatorExpertise, setCreatorExpertise] = useState("");
  const [creatorPlatform, setCreatorPlatform] = useState("");
  const [showDiscoverQuestions, setShowDiscoverQuestions] = useState(true);
  
  // Nexis import state
  const [nexisProjects, setNexisProjects] = useState<NexisProject[]>([]);
  const [loadingNexis, setLoadingNexis] = useState(false);
  const [showNexisDialog, setShowNexisDialog] = useState(false);

  // Form state
  const [script, setScript] = useState("");
  const [niche, setNiche] = useState("");
  const [audienceType, setAudienceType] = useState("");
  const [platform, setPlatform] = useState("");
  const [skillLevel, setSkillLevel] = useState("");
  const [goal, setGoal] = useState("");
  const [idea, setIdea] = useState("");
  const [painPoints, setPainPoints] = useState("");

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  // Cleanup thinking interval on unmount
  useEffect(() => {
    return () => {
      if (thinkingInterval.current) clearInterval(thinkingInterval.current);
    };
  }, []);

  const fetchProject = async () => {
    try {
      const { data, error } = await supabase
        .from("monetization_projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      setProject(data);

      // Pre-fill form
      if (data.script_input) setScript(data.script_input);
      if (data.niche) setNiche(data.niche);
      if (data.audience_type) setAudienceType(data.audience_type);
      if (data.platform) setPlatform(data.platform);
      if (data.skill_level) setSkillLevel(data.skill_level);
      if (data.goal) setGoal(data.goal);
      if (data.idea_input) setIdea(data.idea_input);
      if (data.audience_pain_points) setPainPoints(data.audience_pain_points.join("\n"));
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  const fetchNexisProjects = async () => {
    if (!user) return;
    setLoadingNexis(true);
    try {
      const { data, error } = await supabase
        .from("nexis_projects")
        .select("id, title, script, created_at")
        .eq("user_id", user.id)
        .not("script", "is", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setNexisProjects(data || []);
    } catch (error) {
      console.error("Error fetching Nexis projects:", error);
      toast.error("Failed to load Nexis projects");
    } finally {
      setLoadingNexis(false);
    }
  };

  const handleImportFromNexis = (nexisProject: NexisProject) => {
    if (nexisProject.script) {
      setScript(nexisProject.script);
      setShowNexisDialog(false);
      toast.success(`Imported script from "${nexisProject.title}"`);
    }
  };
  const THINKING_STEPS = [
    { icon: Search, text: "Analysing current market trends..." },
    { icon: TrendingUp, text: "Scanning profitable niches..." },
    { icon: BarChart3, text: "Evaluating opportunity scores..." },
    { icon: Zap, text: "Building your opportunity list..." },
  ];

  const startThinkingAnimation = () => {
    setThinkingStep(0);
    let step = 0;
    thinkingInterval.current = setInterval(() => {
      step++;
      if (step < THINKING_STEPS.length) {
        setThinkingStep(step);
      } else {
        // Stay on last step
        if (thinkingInterval.current) clearInterval(thinkingInterval.current);
      }
    }, 1500);
  };

  const handleDiscoverOpportunities = async () => {
    setGenerating(true);
    setOpportunities([]);
    setSelectedOpportunity(null);
    setShowDiscoverQuestions(false);
    startThinkingAnimation();
    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "discover-opportunities",
          params: {
            audienceStruggle: audienceStruggle.trim() || undefined,
            creatorExpertise: creatorExpertise.trim() || undefined,
            creatorPlatform: creatorPlatform.trim() || undefined,
          },
        },
      });

      if (error) throw error;
      setOpportunities(data.opportunities || []);
    } catch (error) {
      console.error("Error discovering opportunities:", error);
      toast.error("Failed to discover opportunities");
    } finally {
      if (thinkingInterval.current) clearInterval(thinkingInterval.current);
      setGenerating(false);
    }
  };

  const handleSelectOpportunity = (opportunity: any) => {
    setSelectedOpportunity(opportunity);
    setNiche(opportunity.niche);
    setAudienceType(opportunity.audience);
    setIdea(opportunity.headline + ": " + opportunity.whyItsHot);
  };

  const handleContinue = async () => {
    if (!project) return;

    // Validate based on entry type
    if (project.entry_type === "script" && !script.trim()) {
      toast.error("Please enter or paste your script");
      return;
    }
    if (project.entry_type === "idea" && !niche.trim()) {
      toast.error("Please enter your niche");
      return;
    }
    if (project.entry_type === "discover" && !selectedOpportunity && opportunities.length === 0) {
      await handleDiscoverOpportunities();
      return;
    }

    setGenerating(true);
    try {
      // Update project with input
      const updateData: any = {
        script_input: script || null,
        niche: niche || null,
        audience_type: audienceType || null,
        platform: platform || null,
        skill_level: skillLevel || null,
        goal: goal || null,
        idea_input: idea || null,
        audience_pain_points: painPoints ? painPoints.split("\n").filter(p => p.trim()) : null,
        last_step: "products",
        title: project.entry_type === "script" 
          ? script.slice(0, 50) + "..." 
          : niche || project.title,
      };

      const { error: updateError } = await supabase
        .from("monetization_projects")
        .update(updateData)
        .eq("id", projectId);

      if (updateError) throw updateError;

      navigate(`/app/monetization-ai/project/${projectId}/products`);
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Failed to save input");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/app/monetization-ai")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">
              {project.entry_type === "script" && "Paste Your Script"}
              {project.entry_type === "idea" && "Define Your Idea"}
              {project.entry_type === "discover" && "Discover Opportunities"}
            </h1>
            <p className="text-muted-foreground">
              {project.entry_type === "script" && "AI will analyze and find monetization opportunities"}
              {project.entry_type === "idea" && "Tell us about your niche and audience"}
              {project.entry_type === "discover" && "Let AI find profitable opportunities for you"}
            </p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ml-14">
          <MonetizationStepper currentStep="input" />
        </div>

        {/* Script Entry */}
        {project.entry_type === "script" && (
          <div className="space-y-6">
            <div className="glass-card p-6 border-green-500/20">
              <div className="flex items-center justify-between mb-4">
                <Label htmlFor="script" className="text-lg font-semibold">
                  Paste Your Script
                </Label>
                <Dialog open={showNexisDialog} onOpenChange={(open) => {
                  setShowNexisDialog(open);
                  if (open) fetchNexisProjects();
                }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <img src={nexisLogo} alt="Nexis" className="w-4 h-4" />
                      Import from Nexis AI
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <img src={nexisLogo} alt="Nexis" className="w-6 h-6" />
                        Import Script from Nexis AI
                      </DialogTitle>
                    </DialogHeader>
                    <div className="mt-4">
                      {loadingNexis ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                        </div>
                      ) : nexisProjects.length === 0 ? (
                        <div className="text-center py-8">
                          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground mb-4">No scripts found in Nexis AI</p>
                          <Button variant="outline" onClick={() => navigate("/app/nexis-ai")}>
                            Go to Nexis AI
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                          {nexisProjects.map((np) => (
                            <div
                              key={np.id}
                              onClick={() => handleImportFromNexis(np)}
                              className="p-4 rounded-lg border border-border/50 hover:border-purple-500/50 hover:bg-purple-500/5 cursor-pointer transition-all"
                            >
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-foreground">{np.title}</h4>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(np.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {np.script?.slice(0, 150)}...
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <Textarea
                id="script"
                value={script}
                onChange={(e) => setScript(e.target.value)}
                placeholder="Paste your script from Nexis AI or any other source..."
                className="min-h-[300px] bg-background/50"
              />
              <p className="text-sm text-muted-foreground mt-2">
                AI will analyze the topic, audience intent, problems solved, and monetization potential.
              </p>
            </div>

            {/* Optional refinements */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass-card p-4 border-green-500/20">
                <Label>Platform (Optional)</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="glass-card p-4 border-green-500/20">
                <Label>Audience Skill Level (Optional)</Label>
                <Select value={skillLevel} onValueChange={setSkillLevel}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_LEVELS.map((level) => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Own Idea Entry */}
        {project.entry_type === "idea" && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass-card p-4 border-green-500/20">
                <Label htmlFor="niche">Your Niche *</Label>
                <Input
                  id="niche"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  placeholder="e.g., Personal finance for millennials"
                  className="mt-2"
                />
              </div>
              <div className="glass-card p-4 border-green-500/20">
                <Label htmlFor="audience">Target Audience</Label>
                <Input
                  id="audience"
                  value={audienceType}
                  onChange={(e) => setAudienceType(e.target.value)}
                  placeholder="e.g., Young professionals, ages 25-35"
                  className="mt-2"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass-card p-4 border-green-500/20">
                <Label>Platform</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="glass-card p-4 border-green-500/20">
                <Label>Audience Skill Level</Label>
                <Select value={skillLevel} onValueChange={setSkillLevel}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {SKILL_LEVELS.map((level) => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="glass-card p-4 border-green-500/20">
              <Label>Your Goal</Label>
              <Select value={goal} onValueChange={setGoal}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="What do you want to achieve?" />
                </SelectTrigger>
                <SelectContent>
                  {GOALS.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="glass-card p-4 border-green-500/20">
              <Label htmlFor="idea">Your Idea or Expertise (Optional)</Label>
              <Textarea
                id="idea"
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                placeholder="Describe your skill, experience, or product idea..."
                className="mt-2 min-h-[100px]"
              />
            </div>

            <div className="glass-card p-4 border-green-500/20">
              <Label htmlFor="painPoints">Audience Pain Points (Optional)</Label>
              <Textarea
                id="painPoints"
                value={painPoints}
                onChange={(e) => setPainPoints(e.target.value)}
                placeholder="Enter one pain point per line..."
                className="mt-2 min-h-[100px]"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Enter one pain point per line
              </p>
            </div>
          </div>
        )}

        {/* Discover Entry */}
        {project.entry_type === "discover" && (
          <div className="space-y-6">
            {/* Audience Discovery Questions */}
            {showDiscoverQuestions && opportunities.length === 0 && !generating && (
              <div className="space-y-4">
                <div className="glass-card p-6 border-green-500/20">
                  <h3 className="font-display text-lg font-semibold mb-1">Quick Questions</h3>
                  <p className="text-sm text-muted-foreground mb-4">Help AI find better opportunities for you (optional)</p>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="audienceStruggle">What does your audience struggle with most?</Label>
                      <Input
                        id="audienceStruggle"
                        value={audienceStruggle}
                        onChange={(e) => setAudienceStruggle(e.target.value)}
                        placeholder="e.g. Growing on social media, making money online, staying consistent..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="creatorExpertise">What do you know or do better than most people?</Label>
                      <Input
                        id="creatorExpertise"
                        value={creatorExpertise}
                        onChange={(e) => setCreatorExpertise(e.target.value)}
                        placeholder="e.g. Video editing, personal finance, fitness coaching..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="creatorPlatform">What platform are you on?</Label>
                      <Select value={creatorPlatform} onValueChange={setCreatorPlatform}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          {["TikTok", "Instagram", "YouTube", "Twitter/X", "Newsletter", "Other"].map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <Button
                  variant="hero"
                  size="lg"
                  onClick={handleDiscoverOpportunities}
                  className="w-full"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Discover Opportunities
                </Button>
              </div>
            )}

            {/* Animated thinking state */}
            {generating && (
              <div className="glass-card p-8 border-green-500/20">
                <div className="flex flex-col items-center mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-green-400 animate-pulse" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-1">AI is thinking...</h3>
                  <p className="text-sm text-muted-foreground">Generating fresh opportunities just for you</p>
                </div>
                <div className="space-y-3 max-w-md mx-auto">
                  {THINKING_STEPS.map((step, i) => {
                    const StepIcon = step.icon;
                    const isActive = i === thinkingStep;
                    const isDone = i < thinkingStep;
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-500 ${
                          isActive ? "bg-green-500/10 border border-green-500/30" :
                          isDone ? "bg-muted/50 opacity-60" : "opacity-30"
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isDone ? "bg-green-500/20" : isActive ? "bg-green-500/20" : "bg-muted"
                        }`}>
                          {isDone ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : isActive ? (
                            <StepIcon className="w-4 h-4 text-green-400 animate-pulse" />
                          ) : (
                            <StepIcon className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className={`text-sm font-medium ${
                          isActive ? "text-foreground" : isDone ? "text-muted-foreground" : "text-muted-foreground"
                        }`}>
                          {step.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Opportunities grid */}
            {!generating && opportunities.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-semibold">
                    {opportunities.length} Opportunities Found
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDiscoverOpportunities}
                    disabled={generating}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate Opportunities
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {opportunities.map((opp, i) => (
                    <div
                      key={i}
                      onClick={() => handleSelectOpportunity(opp)}
                      className={`glass-card p-5 cursor-pointer transition-all ${
                        selectedOpportunity?.headline === opp.headline
                          ? "border-green-500 bg-green-500/10 ring-2 ring-green-500/20"
                          : "border-border/50 hover:border-green-500/50"
                      }`}
                    >
                      {/* Score badge */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-primary/20 text-primary">
                            {opp.industry}
                          </span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                            opp.difficulty === "easy" ? "bg-green-500/20 text-green-400" :
                            opp.difficulty === "medium" ? "bg-yellow-500/20 text-yellow-400" :
                            "bg-red-500/20 text-red-400"
                          }`}>
                            {opp.difficulty}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-green-400">
                          {opp.score}/10
                        </span>
                      </div>

                      {/* Headline */}
                      <h4 className="font-display font-semibold text-foreground mb-2 leading-snug">
                        {opp.headline}
                      </h4>

                      {/* Niche */}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground inline-block mb-2">
                        {opp.niche}
                      </span>

                      {/* Why it's hot */}
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                        🔥 {opp.whyItsHot}
                      </p>

                      {/* Meta row */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="px-2 py-0.5 rounded-full bg-accent/50 text-accent-foreground">
                          {opp.productFormat}
                        </span>
                        <span className="font-medium text-green-400">{opp.monetizationLabel}</span>
                      </div>

                      {/* Select button */}
                      <Button
                        variant={selectedOpportunity?.headline === opp.headline ? "default" : "outline"}
                        size="sm"
                        className="w-full mt-3"
                        onClick={(e) => { e.stopPropagation(); handleSelectOpportunity(opp); }}
                      >
                        {selectedOpportunity?.headline === opp.headline ? (
                          <><Check className="w-4 h-4 mr-1" /> Selected</>
                        ) : (
                          "Select This Opportunity"
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Continue Button - hidden for discover when no opportunity selected */}
        {!(project.entry_type === "discover" && !selectedOpportunity) && (
          <div className="flex justify-end mt-8">
            <Button
              variant="hero"
              size="lg"
              onClick={handleContinue}
              disabled={generating}
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {project.entry_type === "discover" ? "Continue with Selection" : "Generate Product Ideas"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
